import React, { useEffect, useState } from "react";
import { Organization } from "../../../Entities/Organization";
import AppCard from "./AppCard";
import { ATSApp, ConnectedApp } from "../../../Interface";
import { useZohoRecruitConnect } from "../hooks/useZohoRecruitConnect";
import appIntegrationService from "../../../Service/app.integration.service";
import { openNotification } from "../../../Components/Notification";
import { useLeverHireConnect } from "../hooks/useLeverHireConnect";
import { useLoader } from "../../../Context/LoaderContext";

interface AppsProps {
  organizationDetails: Organization;
}

const apps: ATSApp[] = [
  {
    id: "lever-hire",
    name: "Lever",
    icon: "/images/lever-logo.png",
    description:
      "Integrate your Lever with QualifyMe.ai to enhance collaboration, automate candidate evaluation, and improve hiring decisions across your team.",
    type: "lever",
  },
  {
    id: "zoho-recruit",
    name: "Zoho Recruit",
    icon: "/images/zoho-recruit-logo.png",
    description:
      "Connect your Zoho Recruit with QualifyMe.ai to centralize candidate data, automate profile matching, and streamline your recruitment workflow.",
    type: "zoho",
  },
];

const Apps: React.FC<AppsProps> = ({ organizationDetails }) => {
  const [connectedApps, setConnectedApps] = useState<ConnectedApp[]>([]);

  const { setLoading, setLoadingMessage } = useLoader();
  const { handleZohoRecruitConnect } = useZohoRecruitConnect();
  const { handleLeverHireConnect } = useLeverHireConnect();

  const handleConnectClick = async (app: ATSApp) => {
    if (app.type === "zoho") {
      await handleZohoRecruitConnect();
    } else if (app.type === "lever") {
      await handleLeverHireConnect();
    }
    return;
  };

  const handleDisconnectClick = async (
    appType: "zoho" | "lever" | "custom"
  ) => {
    const userApp = connectedApps.find((app) => app.appType === appType);
    if (!userApp) {
      openNotification("error", "Error", "App not found");
      return;
    }
    const response = await appIntegrationService.disconnectApp(userApp._id);
    if (!response?.success) {
      openNotification("error", "Error", response.message);
      return;
    }
    setConnectedApps(connectedApps.filter((app) => app._id !== userApp._id));
    openNotification("success", "Success", "App disconnected successfully");
    return;
  };

  const handleGenerateAPIKeyClick = async (
    appType: "zoho" | "lever" | "custom"
  ) => {
    const userApp = connectedApps.find((app) => app.appType === appType);
    if (!userApp) {
      openNotification("error", "Error", "App not found");
      return;
    }
    const response = await appIntegrationService.generateAPIKey(userApp._id);
    if (!response?.success) {
      openNotification("error", "Error", response.message);
      return;
    }
    setConnectedApps(
      connectedApps.map((app) =>
        app._id === userApp._id ? { ...app, apiKey: response.data.apiKey } : app
      )
    );
    openNotification("success", "Success", "API key generated successfully");
  };

  useEffect(() => {
    const fetchConnectedApps = async () => {
      setLoading(true);
      setLoadingMessage("Loading connected apps...");
      const response = await appIntegrationService.getAllConnectedApps();
      if (!response?.success) {
        setLoading(false);
        setLoadingMessage(null);
        openNotification("error", "Error", response.message);
        return;
      }
      setConnectedApps(response.data);
      setLoading(false);
      setLoadingMessage(null);
    };
    fetchConnectedApps();
  }, []);

  return (
    <section className="apps-container">
      {apps.map((app) => (
        <AppCard
          key={app.id}
          app={app}
          handleConnectClick={handleConnectClick}
          isConnected={
            connectedApps.find((ca) => ca.appType === app.type) ? true : false
          }
          handleDisconnectClick={handleDisconnectClick}
          handleGenerateAPIKeyClick={handleGenerateAPIKeyClick}
          apiKey={
            connectedApps.find((ca) => ca.appType === app.type)?.apiKey || null
          }
        />
      ))}
    </section>
  );
};

export default Apps;
